import { DataGridPro, GridCallbackDetails, GridCellParams, GridRowParams, MuiEvent } from "@mui/x-data-grid-pro";
import { Box, Tooltip } from "@mui/material";
import { IOpsLinkMessage, IOpsLinkTopic, IFlight } from "@vmosolutions/oc-interfaces";
import { useMemo } from "react";
import _ from "lodash";
import { useSelector } from "react-redux";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { FlightChip } from "src/components/Flights/FlightChip";
import { useTheme, Theme } from "@mui/material/styles";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import { useRetrieveUserName } from "./shared/useRetrieveUserName";
import { buildAcknowledgementTooltip } from "./shared/acknowledgementTooltip";
import { getAcknowledgementState, IAcknowledgementState } from "./shared/acknowledgementState";

interface IOpsLinkEventTable {
  id: string;
  selectedEvents: IOpsLinkMessage[];
  setCurrentEvent: React.Dispatch<React.SetStateAction<IOpsLinkMessage>>;
  disabledPriorities: string[];
  containerHeight: number;
  setReplyEvent: React.Dispatch<React.SetStateAction<IOpsLinkMessage>>;
  searchVal: string;
  trackedFlights: IFlight[];
}

const getAcknowledgedStatusIcon = (state: IAcknowledgementState, theme: Theme): JSX.Element => {
  if (!state.isFromCrew) {
    return state.hasBeenAcknowledged ? (
      <CheckCircleIcon sx={{ color: theme.palette.success.main, fontSize: 18 }} />
    ) : (
      <RemoveCircleOutlineIcon sx={{ color: theme.palette.text.disabled, fontSize: 18 }} />
    );
  }
  if (state.currentUserAcknowledged) {
    return <CheckCircleIcon sx={{ color: theme.palette.success.main, fontSize: 18 }} />;
  }
  return state.hasBeenAcknowledged ? (
    <RadioButtonUncheckedIcon sx={{ color: theme.palette.text.disabled, fontSize: 18 }} />
  ) : (
    <RadioButtonUncheckedIcon sx={{ color: theme.palette.warning.main, fontSize: 18 }} />
  );
};

export const OpsLinkEventTable = (props: IOpsLinkEventTable): JSX.Element => {
  const {
    id,
    selectedEvents,
    setCurrentEvent,
    setReplyEvent,
    disabledPriorities,
    containerHeight,
    searchVal,
    trackedFlights,
  } = props;
  const theme = useTheme();
  const currentUserId = useSelector((state: IRootState) => state.authentication.attributes?._id);
  const getAcknowledgedUserName = useRetrieveUserName();

  const filteredEvents = useMemo(() => {
    const tempEvents = _.cloneDeep(selectedEvents);
    let filteredList = tempEvents.filter((event: IOpsLinkMessage) => !disabledPriorities.includes(event?.priority));

    if (searchVal && searchVal.length >= 3) {
      filteredList = filteredList.filter((event: IOpsLinkMessage) => {
        return event.text.toLowerCase().includes(searchVal.toLowerCase());
      });
    }
    return filteredList;
  }, [disabledPriorities, selectedEvents, searchVal]);

  return (
    <Box sx={{ marginTop: "10px", width: "100%", height: `${containerHeight}px` }}>
      <DataGridPro
        initialState={{
          columns: {
            columnVisibilityModel: {
              eventTimestamp: true,
              priority: true,
              source: true,
              topic: true,
              eventType: true,
            },
          },
          sorting: {
            sortModel: [{ field: "eventTimestamp", sort: "desc" }],
          },
        }}
        columns={[
          {
            flex: 1,
            field: "flight",
            headerName: "Flight",
            type: "string",
            hideable: false,
            renderCell: (params: GridCellParams): JSX.Element => {
              return <FlightChip data={trackedFlights.find((flight: IFlight) => flight._id === params.row.flight)} />;
            },
          },
          {
            flex: 1,
            field: "eventTimestamp",
            headerName: "Timestamp",
            type: "string",
            hideable: false,
          },
          {
            flex: 1,
            field: "priority",
            headerName: "Priority",
            type: "string",
            hideable: false,
            renderCell: (params: GridCellParams): string => params.row.priority?.toUpperCase(),
          },
          {
            flex: 1,
            field: "source",
            headerName: "Source",
            type: "string",
            hideable: false,
          },
          {
            flex: 1,
            field: "topic.shortName",
            headerName: "Topic",
            type: "string",
            hideable: false,
            valueGetter: (params, row) => {
              const topic = row.topic as IOpsLinkTopic;
              return topic.shortName;
            },
          },
          {
            flex: 1,
            field: "eventType",
            headerName: "Subject",
            type: "string",
            hideable: false,
          },
          {
            width: 70,
            field: "ackStatus",
            headerName: "Ack'd",
            hideable: false,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            renderCell: (params: GridCellParams): JSX.Element => {
              const row = params.row as IOpsLinkMessage;
              const topic = row.topic as IOpsLinkTopic;
              if (!topic?.acknowledgeTracking) return <Box />;
              const ackState = getAcknowledgementState(row, currentUserId);
              const tooltipText = buildAcknowledgementTooltip(
                ackState.role,
                Array.isArray(row.acknowledgedBy) ? row.acknowledgedBy : [],
                getAcknowledgedUserName
              );
              return (
                <Tooltip title={<Box sx={{ whiteSpace: "pre-line" }}>{tooltipText}</Box>}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "100%",
                      height: "100%",
                    }}
                  >
                    {getAcknowledgedStatusIcon(ackState, theme)}
                  </Box>
                </Tooltip>
              );
            },
          },
        ]}
        rows={filteredEvents}
        density="compact"
        onRowClick={(params: GridRowParams, event: MuiEvent, details: GridCallbackDetails) => {
          setReplyEvent(null);
          setCurrentEvent(params.row as IOpsLinkMessage);
        }}
        pagination
        getRowClassName={(params) => (params.row.priority === "urgent" ? "urgent-row" : "")}
        sx={{
          "& .urgent-row": {
            backgroundColor: theme.palette.error.main,
            color: theme.palette.error.contrastText,
            "&:hover": {
              backgroundColor: theme.palette.error.dark,
            },
          },
          "& .urgent-row.Mui-selected": {
            backgroundColor: theme.palette.error.dark + " !important",
            color: theme.palette.error.contrastText,
          },
          "& .urgent-row.Mui-selected:hover": {
            backgroundColor: theme.palette.error.dark + " !important",
          },
        }}
      />
    </Box>
  );
};
